import { Outlet, NavLink, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { CSSTransition } from 'react-transition-group'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterBreadcrumbsTimelineAsset from '@/matter/components/matter-breadcrumbs/MatterBreadcrumbsTimelineAsset'
import ErrorComponent from '@/components/error/error-component'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Button } from '@/components/button/Button'
import { useHubspotConversations } from '@/context/useHubspotConversations'
import { useViewportSizeInPixels } from '@/hooks/use-viewport-size-in-pixels'
import ContentViewerColumn from '@/content-viewer/components/ContentViewerColumn'
import ContentViewerSlideover from '@/content-viewer/components/ContentViewerSlideover'
import { ContentViewerActions } from '@/content-viewer/store/slice'
import { selectIsContentViewerOpen } from '@/content-viewer/store/selectors'
import { DEVICE_TYPE_BREAKPOINTS } from '@/hooks/useDeviceType'
import { CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS } from '@/medical-common/constants'
import { AddDocuments } from '@/assets/icons'
import { matterPaths } from '@/routing/paths'
import { formatLastActivityForCell } from '@/matter/utils/format-last-activity'
import { MatterAssetFeatureProvider } from '@/matter-asset-common/feature/MatterAssetFeatureProvider'
import { getTimelineAssetTabName, TimelineAssetTabs } from '../constants'
import { timelineFeatureConfig } from '../TimelineFeatureConfig'
import { TimelineAssetActions } from '../store/slice'
import { selectIsUpdateActive, selectTimelineAssetError, selectTimelineAssetForMatter, selectUpdateStatusForAsset } from '../store/selectors'
import { TimelineUpdateStatus } from '../store/schemas'
import TimelineAssetEmptyState from './TimelineAssetEmptyState'
import TimelineAssetBanners from './TimelineAssetBanners'
import TimelineAssetProcessingSkeleton from './TimelineAssetProcessingSkeleton'
import { TimelineVersionHistoryPopover } from './version-history/TimelineVersionHistoryPopover'

const TABS = [
  { name: getTimelineAssetTabName(TimelineAssetTabs.ALL_EVENTS), path: TimelineAssetTabs.ALL_EVENTS, id: 'all_events_tab' },
  { name: getTimelineAssetTabName(TimelineAssetTabs.SOURCE_FILES), path: TimelineAssetTabs.SOURCE_FILES, id: 'source_files_tab' },
]

function TimelineBetaBannerTarget() {
  return (
    <div id="timeline-beta-banner-target" className="relative grow-0 shrink-0 w-full">
      <div id="timeline-beta-banner-target-child" className="min-h-px w-full" />
    </div>
  )
}

/**
 * Layout for the matter's living Timeline.
 *
 * Branches on the asset state: loading, error, empty (no timeline yet), first build in
 * flight (skeleton), or the tabbed timeline with status banners and the content viewer
 * split (column on desktop, slideover on mobile).
 */
export default function TimelineAssetLayout() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { toggleWidget } = useHubspotConversations()
  // Lifted so the failed-files banner's "Version info" action can open the version-history panel.
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false)

  const { width: viewportWidth } = useViewportSizeInPixels()
  const useColumnLayout = viewportWidth >= DEVICE_TYPE_BREAKPOINTS.DESKTOP

  const { matterId } = useParams<{ matterId: string }>()
  const asset = useAppSelector((state: RootState) => selectTimelineAssetForMatter(state, { matterId }))
  const assetError = useAppSelector((state: RootState) => selectTimelineAssetError(state, { matterId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))
  const updateStatus = useAppSelector((state: RootState) => selectUpdateStatusForAsset(state, { assetId: asset?.id }))
  const showContentViewer = useAppSelector((state: RootState) => selectIsContentViewerOpen(state))

  const referenceColumnRef = useRef<HTMLDivElement>(null)

  // The first build has no live events yet - show the skeleton until it publishes
  const isFirstBuildInFlight = Boolean(asset && asset.chronology_version === 0 && isUpdateActive)
  // The first build errored with nothing published - a full error state replaces the empty table
  const isFirstBuildError = Boolean(asset && asset.chronology_version === 0 && !isUpdateActive && updateStatus?.update.status === TimelineUpdateStatus.ERROR)

  // Clear content viewer when component unmounts
  useEffect(() => {
    return () => {
      dispatch(ContentViewerActions.clearContentViewer())
    }
  }, [dispatch])

  return (
    <div className="grow flex flex-col w-full h-full overflow-hidden">
      <MatterBreadcrumbsTimelineAsset />
      {matterId && <TimelineBetaBannerTarget />}

      {/* Loading the overview for the first time */}
      {asset === undefined && !assetError && (
        <div className="flex-1 flex items-center justify-center">
          <CircularProgressContinuousSized size={20} thickness={7} />
        </div>
      )}

      {/* Overview failed to load */}
      {asset === undefined && assetError && (
        <ErrorComponent
          code="500"
          title="Error Loading Timeline"
          message={
            <>
              {'Our servers ran into a problem. Please '}
              <button
                onClick={() => matterId && dispatch(TimelineAssetActions.fetchAssetForMatter({ matterId }))}
                className="text-brand-500 hover:text-brand-400 underline cursor-pointer"
              >
                try again
              </button>
              {', or '}
              <button onClick={toggleWidget} className="text-brand-500 hover:text-brand-400 underline cursor-pointer">
                contact support
              </button>
              {' if the issue continues.'}
            </>
          }
        />
      )}

      {/* No timeline yet */}
      {asset === null && matterId && <TimelineAssetEmptyState matterId={matterId} />}

      {/* The living timeline */}
      {asset && matterId && (
        <MatterAssetFeatureProvider config={timelineFeatureConfig} storeKey={asset.id}>
          <div className="flex flex-1 overflow-hidden">
            {/* Left: banners + tabbed content area */}
            <div
              className={clsx(
                'flex flex-col max-w-none pt-2 lg:pt-3.5 lg:pl-2 transition-[width] duration-200 ease-out',
                showContentViewer && useColumnLayout ? 'w-1/2' : 'w-full'
              )}
            >
              <TimelineAssetBanners matterId={matterId} assetId={asset.id} onShowVersionInfo={() => setVersionHistoryOpen(true)} />

              <div className="grow-0 shrink-0 items-end flex relative gap-1 pl-1 lg:pl-0 z-10 -mb-px">
                {/* Tabs scroll horizontally on narrow screens so the pinned actions stay reachable */}
                <div className="flex-1 min-w-0 flex items-end gap-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                  {TABS.map((tab) => (
                    <NavLink
                      key={tab.path}
                      to={tab.path}
                      id={tab.id}
                      className={({ isActive }) =>
                        `relative px-3 py-1 flex items-center gap-1 text-sm font-semibold tracking-[-0.15px] transition-all duration-150 ease-in-out rounded-t-md border whitespace-nowrap ${
                          isActive
                            ? 'bg-brand-neutral-50 text-brand-800 border-b-transparent'
                            : 'bg-brand-neutral-100 text-brand-neutral-600 hover:bg-brand-neutral-200'
                        }`
                      }
                    >
                      {tab.name}
                    </NavLink>
                  ))}
                </div>

                {/* Right-side actions — pinned so they stay visible/reachable even when tabs overflow */}
                <div className="shrink-0 flex items-center gap-3 pl-2 pr-2 pb-1">
                  {asset.last_built_at && (
                    <>
                      <span className="hidden sm:inline text-xs font-medium text-brand-neutral-600 whitespace-nowrap">
                        {formatLastActivityForCell(asset.last_built_at)}
                      </span>
                      <span className="hidden sm:block h-6 w-px bg-brand-neutral-200" aria-hidden />
                    </>
                  )}
                  <Button
                    onClick={() => navigate(matterPaths.timeline.addFiles(matterId))}
                    disabled={isUpdateActive}
                    variant="tonal"
                    color="neutral"
                    size="sm"
                    leadingIcon={<AddDocuments />}
                    title={isUpdateActive ? 'An update is already in progress' : undefined}
                  >
                    Add Files
                  </Button>
                  <TimelineVersionHistoryPopover assetId={asset.id} open={versionHistoryOpen} onOpenChange={setVersionHistoryOpen} />
                </div>
              </div>

              <div className="overflow-hidden flex-1 min-h-0 flex flex-col lg:border-l border-t bg-brand-neutral-50">
                <div className="flex flex-col flex-1 h-full">
                  {isFirstBuildInFlight ? (
                    <TimelineAssetProcessingSkeleton />
                  ) : isFirstBuildError && updateStatus ? (
                    <ErrorComponent
                      code="500"
                      title="Timeline Failed to Generate"
                      message={
                        <>
                          {'Something went wrong while building the timeline. Error ID: '}
                          <span className="font-mono">{updateStatus.update.id}</span>
                          {'. Please try again, or '}
                          <button onClick={toggleWidget} className="text-brand-500 hover:text-brand-400 underline cursor-pointer">
                            contact support
                          </button>
                          {' with this ID if the issue continues.'}
                        </>
                      }
                      customLink={{ label: 'Try Again', to: matterPaths.timeline.addFiles(matterId) }}
                    />
                  ) : (
                    <Outlet />
                  )}
                </div>
              </div>
            </div>

            {/* Right: Content Viewer column */}
            <CSSTransition
              appear
              nodeRef={referenceColumnRef}
              in={showContentViewer}
              timeout={{ enter: CONTENT_VIEWER_COLUMN_TRANSITION_DURATION_MS, exit: 0 }}
              classNames="content-viewer-column"
              unmountOnExit
            >
              <div
                ref={referenceColumnRef}
                className={clsx('overflow-hidden flex flex-col h-full transform-gpu will-change-transform', useColumnLayout ? 'w-1/2' : 'w-0')}
              >
                <ContentViewerColumn conversationId="none" />
              </div>
            </CSSTransition>

            {/* Content viewer as slideover - small screens */}
            {!useColumnLayout && <ContentViewerSlideover open={showContentViewer} conversationId="none" />}
          </div>
        </MatterAssetFeatureProvider>
      )}
    </div>
  )
}
